import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, setImpersonationToken } from '@/lib/api'

const STORAGE_KEY = 'sevfy_impersonation'

interface ImpersonationSession {
  token: string
  workspaceId: string
  workspaceName: string
  staffRole: string
  isReadOnly: boolean
  expiresAt: string
}

export const useImpersonationStore = defineStore('impersonation', () => {
  // Restore session from sessionStorage so it survives page refreshes within the tab
  const session = ref<ImpersonationSession | null>(
    JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null')
  )

  const isImpersonating = computed(() => !!session.value)
  const workspaceName = computed(() => session.value?.workspaceName ?? null)
  const workspaceId = computed(() => session.value?.workspaceId ?? null)
  const isReadOnly = computed(() => session.value?.isReadOnly ?? true)

  // Restore API client state if we already have a session (e.g. after page refresh)
  if (session.value) {
    setImpersonationToken(session.value.token)
    api.setWorkspaceId(session.value.workspaceId)
  }

  /**
   * Validate a token issued by the admin server and store the session.
   * Called by ImpersonateView when the user arrives via the impersonate URL.
   */
  async function startImpersonation(token: string): Promise<void> {
    const data = await api.post<Omit<ImpersonationSession, 'token'>>(
      '/auth/impersonate',
      { token },
      { skipAuth: true }
    )

    session.value = { ...data, token }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session.value))
    setImpersonationToken(token)
    api.setWorkspaceId(data.workspaceId)
  }

  /**
   * End the impersonation session and clean up all state.
   */
  async function endImpersonation(): Promise<void> {
    if (!session.value) return

    try {
      await api.post('/auth/impersonate/end', { token: session.value.token }, { skipAuth: true })
    } catch {
      // best-effort - clear local state regardless
    }

    session.value = null
    sessionStorage.removeItem(STORAGE_KEY)
    setImpersonationToken(null)
    api.setWorkspaceId(null)
  }

  return {
    session,
    isImpersonating,
    workspaceName,
    workspaceId,
    isReadOnly,
    startImpersonation,
    endImpersonation,
  }
})
